import React, { useState, useEffect, useCallback } from 'react';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Plus, Search, MoreHorizontal, UserCheck, UserX, Clock, Shield, Pencil, Trash2, KeyRound } from 'lucide-react';
import { toast } from 'sonner';

const ROLES = ['admin', 'sales', 'support', 'operations', 'hh_global', 'agency', 'meta'];
const ROLE_STYLE = { admin: 'bg-red-500/10 text-red-700 dark:text-red-400', sales: 'bg-blue-500/10 text-blue-700 dark:text-blue-400', support: 'bg-emerald-500/10 text-emerald-700 dark:text-emerald-400', operations: 'bg-amber-500/10 text-amber-700 dark:text-amber-400', hh_global: 'bg-violet-500/10 text-violet-700 dark:text-violet-400', agency: 'bg-teal-500/10 text-teal-700 dark:text-teal-400', meta: 'bg-orange-500/10 text-orange-700 dark:text-orange-400' };

export default function UsersPage() {
  const [users, setUsers] = useState([]);
  const [pendingUsers, setPendingUsers] = useState([]);
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [dlgOpen, setDlgOpen] = useState(false);
  const [approveDlg, setApproveDlg] = useState(null);
  const [approveRole, setApproveRole] = useState('sales');
  const [form, setForm] = useState({ name: '', email: '', password: '', role: 'sales' });
  const [resetDlg, setResetDlg] = useState(null);
  const [newPassword, setNewPassword] = useState('');

  const loadUsers = useCallback(() => {
    api.get('/users', { params: { search, role: roleFilter, status: 'approved' } }).then(r => setUsers(r.data.users || []));
  }, [search, roleFilter]);

  const loadPending = useCallback(() => {
    api.get('/users', { params: { status: 'pending' } }).then(r => setPendingUsers(r.data.users || []));
  }, []);

  useEffect(() => { loadUsers(); loadPending(); }, [loadUsers, loadPending]);

  const createUser = async () => {
    try {
      await api.post('/users', form);
      toast.success('User created (auto-approved)');
      setDlgOpen(false); setForm({ name: '', email: '', password: '', role: 'sales' });
      loadUsers(); loadPending();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const approve = async () => {
    try {
      await api.put(`/users/${approveDlg}/approve`, { role: approveRole });
      toast.success('User approved');
      setApproveDlg(null); loadUsers(); loadPending();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const reject = async (uid) => {
    if (!window.confirm('Reject and remove this user?')) return;
    await api.put(`/users/${uid}/reject`);
    toast.success('User rejected');
    loadPending();
  };

  const updateRole = async (uid, role) => {
    await api.put(`/users/${uid}`, { role });
    toast.success('Role updated');
    loadUsers();
  };

  const del = async (uid) => {
    if (!window.confirm('Delete this user?')) return;
    await api.delete(`/users/${uid}`);
    toast.success('Deleted');
    loadUsers();
  };

  const resetPassword = async () => {
    if (!newPassword || newPassword.length < 6) { toast.error('Password must be at least 6 characters'); return; }
    try {
      await api.put(`/users/${resetDlg}/reset-password`, { new_password: newPassword });
      toast.success('Password reset successfully');
      setResetDlg(null); setNewPassword('');
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to reset password'); }
  };


  return (
    <div data-testid="users-page" className="space-y-4">
      <h1 className="font-heading text-2xl font-bold tracking-tight">User Management</h1>

      <Tabs defaultValue={pendingUsers.length > 0 ? 'pending' : 'users'}>
        <TabsList>
          <TabsTrigger value="pending" data-testid="tab-pending-users">
            <Clock size={14} className="mr-1.5" /> Pending Approval
            {pendingUsers.length > 0 && <Badge variant="destructive" className="ml-1.5 h-5 text-[10px]">{pendingUsers.length}</Badge>}
          </TabsTrigger>
          <TabsTrigger value="users" data-testid="tab-active-users"><Shield size={14} className="mr-1.5" /> Active Users</TabsTrigger>
        </TabsList>

        {/* ═══ PENDING APPROVAL TAB ═══ */}
        <TabsContent value="pending" className="mt-4 space-y-4">
          {pendingUsers.length === 0 ? (
            <Card className="border border-border"><CardContent className="p-8 text-center text-muted-foreground"><UserCheck size={32} className="mx-auto mb-2 opacity-40" /><p>No pending approvals</p></CardContent></Card>
          ) : (
            <div className="space-y-3">
              {pendingUsers.map(u => (
                <Card key={u.user_id} className="border border-amber-500/20" data-testid={`pending-user-${u.user_id}`}>
                  <CardContent className="p-4 flex items-center justify-between">
                    <div>
                      <p className="font-medium">{u.name}</p>
                      <p className="text-sm text-muted-foreground">{u.email}</p>
                      <p className="text-xs text-muted-foreground mt-0.5">Registered: {u.created_at?.slice(0, 10)} | via {u.auth_provider}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button size="sm" variant="outline" className="text-destructive border-destructive/30" onClick={() => reject(u.user_id)} data-testid={`reject-${u.user_id}`}>
                        <UserX size={14} className="mr-1" /> Reject
                      </Button>
                      <Button size="sm" onClick={() => { setApproveDlg(u.user_id); setApproveRole('sales'); }} data-testid={`approve-${u.user_id}`}>
                        <UserCheck size={14} className="mr-1" /> Approve
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        {/* ═══ ACTIVE USERS TAB ═══ */}
        <TabsContent value="users" className="mt-4 space-y-4">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-center gap-3">
              <div className="relative"><Search className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" size={16} /><Input placeholder="Search users..." value={search} onChange={e => setSearch(e.target.value)} className="pl-10 w-56" data-testid="user-search" /></div>
              <Select value={roleFilter || 'all'} onValueChange={v => setRoleFilter(v === 'all' ? '' : v)}>
                <SelectTrigger className="w-36" data-testid="user-role-filter"><SelectValue placeholder="All Roles" /></SelectTrigger>
                <SelectContent><SelectItem value="all">All Roles</SelectItem>{ROLES.map(r => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <Button onClick={() => { setForm({ name: '', email: '', password: '', role: 'sales' }); setDlgOpen(true); }} data-testid="create-user-btn"><Plus size={16} className="mr-1" /> New User</Button>
          </div>

          <div className="rounded-md border border-border">
            <Table>
              <TableHeader><TableRow className="hover:bg-transparent">
                <TableHead>Name</TableHead><TableHead>Email</TableHead><TableHead>Role</TableHead><TableHead>Provider</TableHead><TableHead>Created</TableHead><TableHead className="w-[50px]" />
              </TableRow></TableHeader>
              <TableBody>
                {users.length === 0 && <TableRow><TableCell colSpan={6} className="text-center py-8 text-muted-foreground">No users</TableCell></TableRow>}
                {users.map(u => (
                  <TableRow key={u.user_id} className="hover:bg-muted/50" data-testid={`user-row-${u.user_id}`}>
                    <TableCell className="font-medium">{u.name}</TableCell>
                    <TableCell className="text-sm">{u.email}</TableCell>
                    <TableCell><Badge variant="secondary" className={`text-xs capitalize ${ROLE_STYLE[u.role] || ''}`}>{u.role}</Badge></TableCell>
                    <TableCell className="text-xs text-muted-foreground">{u.auth_provider}</TableCell>
                    <TableCell className="text-xs text-muted-foreground">{u.created_at?.slice(0, 10)}</TableCell>
                    <TableCell>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild><Button variant="ghost" size="icon" className="h-7 w-7"><MoreHorizontal size={14} /></Button></DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          {ROLES.filter(r => r !== u.role).map(r => <DropdownMenuItem key={r} onClick={() => updateRole(u.user_id, r)}>Set {r}</DropdownMenuItem>)}
                          <DropdownMenuSeparator />
                          <DropdownMenuItem onClick={() => { setResetDlg(u.user_id); setNewPassword(''); }}><KeyRound size={12} className="mr-1.5" /> Reset Password</DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem className="text-destructive" onClick={() => del(u.user_id)}><Trash2 size={12} className="mr-1.5" /> Delete</DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </TabsContent>
      </Tabs>

      {/* ─── Create User Dialog ─── */}
      <Dialog open={dlgOpen} onOpenChange={setDlgOpen}>
        <DialogContent data-testid="create-user-dialog">
          <DialogHeader><DialogTitle className="font-heading">New User</DialogTitle></DialogHeader>
          <div className="grid gap-3 py-2">
            <div className="space-y-1.5"><Label>Name *</Label><Input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} data-testid="user-name-input" /></div>
            <div className="space-y-1.5"><Label>Email *</Label><Input type="email" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} data-testid="user-email-input" /></div>
            <div className="space-y-1.5"><Label>Password *</Label><Input type="password" value={form.password} onChange={e => setForm(f => ({ ...f, password: e.target.value }))} data-testid="user-password-input" /></div>
            <div className="space-y-1.5"><Label>Role</Label>
              <Select value={form.role} onValueChange={v => setForm(f => ({ ...f, role: v }))}><SelectTrigger data-testid="user-role-select"><SelectValue /></SelectTrigger><SelectContent>{ROLES.map(r => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent></Select>
            </div>
            <p className="text-xs text-muted-foreground">Admin-created users are automatically approved.</p>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setDlgOpen(false)}>Cancel</Button><Button onClick={createUser} disabled={!form.name || !form.email || !form.password} data-testid="user-create-submit">Create</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ─── Approve Dialog ─── */}
      <Dialog open={!!approveDlg} onOpenChange={() => setApproveDlg(null)}>
        <DialogContent className="sm:max-w-md" data-testid="approve-user-dialog">
          <DialogHeader><DialogTitle className="font-heading">Approve User</DialogTitle></DialogHeader>
          <div className="space-y-1.5 py-2">
            <Label>Assign Role *</Label>
            <Select value={approveRole} onValueChange={setApproveRole}>
              <SelectTrigger data-testid="approve-role-select"><SelectValue /></SelectTrigger>
              <SelectContent>{ROLES.map(r => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground mt-2">The user will be able to log in with this role after approval.</p>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setApproveDlg(null)}>Cancel</Button><Button onClick={approve} data-testid="approve-submit"><UserCheck size={14} className="mr-1" /> Approve</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ─── Reset Password Dialog ─── */}
      <Dialog open={!!resetDlg} onOpenChange={() => setResetDlg(null)}>
        <DialogContent className="sm:max-w-md" data-testid="reset-password-dialog">
          <DialogHeader><DialogTitle className="font-heading">Reset Password</DialogTitle></DialogHeader>
          <div className="space-y-1.5 py-2">
            <Label>New Password *</Label>
            <Input type="password" value={newPassword} onChange={e => setNewPassword(e.target.value)} placeholder="Enter new password (min 6 chars)" data-testid="reset-password-input" />
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setResetDlg(null)}>Cancel</Button><Button onClick={resetPassword} disabled={!newPassword || newPassword.length < 6} data-testid="reset-password-submit"><KeyRound size={14} className="mr-1" /> Reset Password</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
